<template>
  <div ref="readerPageRef" class="reader-page" :class="'theme-' + settings.theme">
    <div class="reader-header" :class="{ 'header-hidden': hideUI }">
      <div class="header-left">
        <button class="back-btn" @click="goBack">
          <span class="arrow">←</span> 返回
        </button>
        <div class="comic-info">
          <div class="comic-title">{{ comicTitle }}</div>
          <div class="chapter-title">{{ chapterTitle }}</div>
        </div>
      </div>
      <div class="header-right">
        <div class="chapter-nav">
          <button class="prev-chapter" @click="goPrevChapter" :disabled="!hasPrevChapter">上一章</button>
          <div class="chapter-select">
            <div class="current-chapter" @click="showChapterList = !showChapterList">
              章节列表 <span class="arrow-down">▼</span>
            </div>
          </div>
          <button class="next-chapter" @click="goNextChapter" :disabled="!hasNextChapter">下一章</button>
        </div>
        <button
          class="reader-settings-btn"
          type="button"
          @click="showSettings = !showSettings"
          :title="`阅读设置（当前：${settings.mode === 'paged' ? '单页' : '滚动'}）`"
        >
          <span aria-hidden="true">⚙</span>
          <span class="reader-settings-label">设置</span>
        </button>
      </div>
    </div>
    
    <div class="reader-content" @click="handleContentClick">
      <!-- 加载中状态 -->
      <div class="loading" v-if="loading">
        <div class="spinner"></div>
        <p>加载中，请稍候...</p>
      </div>
      
      <!-- 需要会员/登录 -->
      <div class="no-permission" v-else-if="!hasPermission">
        <h3>无法查看此章节</h3>
        
        <!-- 登录错误 -->
        <p v-if="!isLoggedIn || (chapterData && chapterData.error && chapterData.errorType === 'login')">请先登录再阅读本章</p>
        
        <!-- VIP章节 -->
        <p v-else-if="isVipChapter && !isVip">此为VIP章节，请开通VIP后阅读</p>
        
        <!-- 付费章节 -->
        <p v-else-if="chapterData && chapterData.error && chapterData.errorType === 'permission'">
          此章节为付费内容，请开通VIP后阅读
        </p>
        
        <!-- 其他错误 -->
        <p v-else-if="chapterData && chapterData.error && chapterData.errorType === 'unknown'">
          {{ chapterData.message || '加载失败，请稍后再试' }}
        </p>
        
        <!-- 青少年模式限制 -->
        <p v-else-if="chapterData && chapterData.error && chapterData.errorType === 'youth'">
          {{ chapterData.message || '青少年模式下不可阅读本章' }}
        </p>
        
        <!-- 默认提示 -->
        <p v-else>此章节为付费内容，请开通VIP后阅读</p>
        
        <div class="action-buttons">
          <button class="action-btn" v-if="!isLoggedIn || (chapterData && chapterData.error && chapterData.errorType === 'login')" @click="goToLogin">立即登录</button>
          <button class="action-btn" v-else-if="chapterData && chapterData.error && chapterData.errorType === 'youth'" @click="goYouthProtection">青少年模式设置</button>
          <button class="action-btn" v-else-if="isVipChapter && !isVip" @click="goToVip">开通VIP</button>
          <button class="action-btn" v-else-if="chapterData && chapterData.error && chapterData.errorType === 'permission'" @click="goToVip">开通VIP</button>
          <button class="action-btn" v-else-if="chapterData && chapterData.error && chapterData.errorType === 'unknown'" @click="fetchChapterDetail">重新加载</button>
          <button class="action-btn" v-else @click="goToVip">开通VIP</button>
        </div>
      </div>
      
      <!-- 显示漫画内容 -->
      <div class="comic-images" :class="{ paged: settings.mode === 'paged' }" v-else-if="hasChapterImages">
          <div 
            v-for="(image, index) in visibleImages" 
            :key="settings.mode === 'paged' ? (pagedShowsCopyright ? 'copyright' : currentPageIndex) : index"
            class="image-container"
            :data-index="visibleImageDataIndex(index)"
          >
            <img 
              class="comic-image" 
              :src="getReaderImageUrl(image)" 
              @load="handleImageLoaded(visibleImageDisplayIndex(index))" 
              @error="handleImageError(visibleImageDisplayIndex(index), $event)"
              alt="漫画图片" 
            />
            <div class="image-loading" v-if="!imageLoaded[visibleImageDisplayIndex(index)] && !imageFailed[visibleImageDisplayIndex(index)]">
              <div class="spinner"></div>
            </div>
          </div>

          <!-- 单页翻页导航 -->
          <div class="paged-nav" v-if="settings.mode === 'paged' && hasChapterImages">
            <button type="button" class="paged-prev" :disabled="pagedShowsCopyright" @click.stop="goPage(-1)">‹ 上一页</button>
            <span class="paged-indicator">{{ pagedIndicatorText }}</span>
            <button type="button" class="paged-next" :disabled="pagedShowsCopyright ? readerImages.length === 0 : currentPageIndex >= readerImages.length - 1" @click.stop="goPage(1)">下一页 ›</button>
          </div>
        </div>
      <div class="reader-empty-state" v-else>
        <img :src="placeholderImage" alt="暂无章节图片">
        <h3>这一话暂时没有图片内容</h3>
        <p>后台还没有上传章节截图，或原图片文件已经缺失。</p>
      </div>
    </div>
    
    <div class="reader-footer" :class="{ 'footer-hidden': hideUI }">
      <div class="footer-nav">
        <button class="prev-chapter" @click="goPrevChapter" :disabled="!hasPrevChapter">上一章</button>
        <div class="chapter-info">
          {{ currentChapterIndex + 1 }}/{{ chapterList.length }}
        </div>
        <button class="next-chapter" @click="goNextChapter" :disabled="!hasNextChapter">下一章</button>
      </div>
    </div>
    
    <button
      type="button"
      class="toggle-ui-btn"
      :aria-label="hideUI ? '显示阅读界面' : '隐藏阅读界面'"
      :title="hideUI ? '显示阅读界面' : '隐藏阅读界面'"
      @click="toggleUI"
    >
      <span aria-hidden="true">{{ hideUI ? '◉' : '◌' }}</span>
    </button>
    
    <button
      type="button"
      class="fullscreen-btn"
      :aria-label="isFullscreen ? '退出全屏' : '进入全屏'"
      :title="isFullscreen ? '退出全屏' : '进入全屏'"
      @click="toggleFullscreen"
    >
      <span aria-hidden="true">{{ isFullscreen ? '⛶' : '⛶' }}</span>
    </button>

    <!-- 章节侧边栏 -->
    <div class="chapter-sidebar" :class="{ 'sidebar-open': showChapterList }">
      <div class="sidebar-header">
        <h3>章节列表</h3>
        <button class="close-sidebar" @click="showChapterList = false">×</button>
      </div>
      <div class="sidebar-content">
        <div 
          v-for="chapter in chapterList" 
          :key="chapter.id" 
          class="sidebar-chapter-item" 
          :class="{ active: parseInt(chapter.id) === parseInt(chapterId) }"
          @click="switchChapter(parseInt(chapter.id))"
        >
          <div class="sidebar-chapter-title">{{ chapter.title }}</div>
          <div class="sidebar-chapter-tag">
            <span class="chapter-tag free" v-if="!chapter.need_pay">免费</span>
            <span class="chapter-tag vip" v-else>VIP</span>
          </div>
        </div>
      </div>
    </div>

    <div class="sidebar-overlay" v-if="showChapterList" @click="showChapterList = false"></div>

    <!-- 阅读设置 -->
    <div class="reader-settings-mask" v-if="showSettings" @click.self="showSettings = false">
      <div class="reader-settings-panel">
        <div class="settings-head">
          <h3>阅读设置</h3>
          <button type="button" class="settings-close" @click="showSettings = false">×</button>
        </div>
        <div class="setting-row">
          <span>背景主题</span>
          <div class="setting-options">
            <button type="button" :class="{ active: settings.theme === 'default' }" @click="applyTheme('default')">默认</button>
            <button type="button" :class="{ active: settings.theme === 'night' }" @click="applyTheme('night')">夜间</button>
            <button type="button" :class="{ active: settings.theme === 'paper' }" @click="applyTheme('paper')">护眼</button>
          </div>
        </div>
        <div class="setting-row">
          <span>翻页模式</span>
          <div class="setting-options">
            <button type="button" :class="{ active: settings.mode === 'scroll' }" @click="applyMode('scroll')">滚动</button>
            <button type="button" :class="{ active: settings.mode === 'paged' }" @click="applyMode('paged')">单页</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, reactive, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import api from '../api/index'
import request from '../api/request'
import comicPlaceholder from '@/assets/comic-placeholder.svg'
import { youthErrorText, YOUTH_CODE_LOCKED } from '../utils/youthContent'

const buildCopyrightNoticeImage = (ageRatingText = '全龄段适宜', copyrightNo = '') => {
  // 版权号（作品登记证书登记号）有值才显示
  const copyrightNoBlock = copyrightNo ? `
  <text x="112" y="925" fill="#8f9dbb" font-size="21" font-family="Microsoft YaHei, PingFang SC, sans-serif">版权号</text>
  <text x="390" y="925" fill="#f5f7ff" font-size="22" font-weight="700" font-family="Arial, Microsoft YaHei, sans-serif">${copyrightNo}</text>` : ''
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 1320" role="img" aria-label="道晗漫画版权声明与作品提示">
  <rect width="900" height="1320" fill="#05060b"/>
  <g fill="#ffe000">
    <rect x="435" y="78" width="30" height="46" rx="5"/>
    <path d="M450 138c-53 0-88 39-88 91v25h-28v22h232v-22h-28v-25c0-52-35-91-88-91zm-63 116c0-36 24-63 63-63s63 27 63 63v25H387z"/>
    <rect x="351" y="276" width="198" height="18" rx="7"/>
    <path d="M338 106l-26-27 18-18 27 27zM562 106l26-27-18-18-27 27z"/>
  </g>
  <rect x="443" y="181" width="14" height="43" rx="5" fill="#05060b"/>
  <circle cx="450" cy="241" r="8" fill="#05060b"/>
  <text x="450" y="374" text-anchor="middle" fill="#ffdf00" font-size="54" font-weight="700" font-family="Microsoft YaHei, PingFang SC, sans-serif">本作品纯属虚构</text>
  <text x="450" y="444" text-anchor="middle" fill="#f5f7ff" font-size="28" font-family="Microsoft YaHei, PingFang SC, sans-serif">作品中出现的人物、事件、团体等</text>
  <text x="450" y="494" text-anchor="middle" fill="#f5f7ff" font-size="28" font-family="Microsoft YaHei, PingFang SC, sans-serif">均为剧情所需设置，</text>
  <text x="450" y="544" text-anchor="middle" fill="#f5f7ff" font-size="28" font-family="Microsoft YaHei, PingFang SC, sans-serif">请勿代入现实。</text>
  <text x="450" y="614" text-anchor="middle" fill="#f5f7ff" font-size="28" font-family="Microsoft YaHei, PingFang SC, sans-serif">本作品坚持正确的价值观，</text>
  <text x="450" y="664" text-anchor="middle" fill="#f5f7ff" font-size="28" font-family="Microsoft YaHei, PingFang SC, sans-serif">切勿模仿不当行为。</text>
  <text x="112" y="752" fill="#23d3ee" font-size="24" font-weight="700" font-family="Microsoft YaHei, PingFang SC, sans-serif">版权与授权信息</text>
  <text x="112" y="858" fill="#8f9dbb" font-size="21" font-family="Microsoft YaHei, PingFang SC, sans-serif">版权归属方</text>
  <text x="390" y="858" fill="#f5f7ff" font-size="22" font-weight="700" font-family="Microsoft YaHei, PingFang SC, sans-serif">© 2026 道晗漫画出品</text>${copyrightNoBlock}
  <text x="112" y="992" fill="#8f9dbb" font-size="21" font-family="Microsoft YaHei, PingFang SC, sans-serif">授权期限</text>
  <text x="390" y="992" fill="#f5f7ff" font-size="22" font-weight="700" font-family="Microsoft YaHei, PingFang SC, sans-serif">2028年</text>
  <text x="112" y="1126" fill="#8f9dbb" font-size="21" font-family="Microsoft YaHei, PingFang SC, sans-serif">适龄提示</text>
  <text x="390" y="1126" fill="#f5f7ff" font-size="22" font-weight="700" font-family="Microsoft YaHei, PingFang SC, sans-serif">${ageRatingText}</text>
</svg>`
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
}

const ageRatingTextOf = (rating) => {
  const map = {
    'all': '全龄段适宜',
    '8+': '8周岁以上适宜',
    '12+': '12周岁以上适宜',
    '16+': '16周岁以上适宜'
  }
  return map[rating] || '全龄段适宜'
}

export default {
  name: 'ComicReader',
  
  setup() {
    const router = useRouter()
    const route = useRoute()
    
    // 数据
    const loading = ref(true)
    const chapterData = ref({})
    const chapterList = ref([])
    const comicTitle = ref('')
    const comicInfo = ref({})
    const readerPageRef = ref(null)
    const showChapterList = ref(false)
    const imageLoaded = reactive({})
    const imageFailed = reactive({})
    const baseUrl = request.baseUrl
    const placeholderImage = comicPlaceholder
    
    // 从路由获取参数
    const comicId = computed(() => route.params.comicId)
    const chapterId = ref(route.params.chapterId)
    const copyrightNoticeImageUrl = computed(() => buildCopyrightNoticeImage(ageRatingTextOf(comicInfo.value.age_rating), comicInfo.value.copyright_no || ''))
    
    console.log('===DEBUG===: 实际路由参数:', {
      raw: route.params,
      comicId: comicId.value,
      chapterId: chapterId.value
    })
    
    // 计算属性
    const chapterTitle = computed(() => {
      // 确保当chapterData为null或undefined时不会出错
      if (!chapterData.value) return '';
      
      // 如果有错误但包含title，则使用该title
      if (chapterData.value.error && chapterData.value.title) {
        return chapterData.value.title;
      }
      
      // 正常情况下返回title，如果没有则返回默认值
      return chapterData.value.title || '';
    })
    const currentChapterIndex = computed(() => {
      if (!chapterList.value || chapterList.value.length === 0 || !chapterId.value) {
        return -1;
      }
      
      // 字符串类型的ID需要转换为数字进行比较
      const currentId = parseInt(chapterId.value);
      return chapterList.value.findIndex(chapter => parseInt(chapter.id) === currentId);
    })
    const hasPrevChapter = computed(() => currentChapterIndex.value > 0)
    const hasNextChapter = computed(() => currentChapterIndex.value < chapterList.value.length - 1)
    const isLoggedIn = computed(() => localStorage.getItem('token') !== null)
    const isVipChapter = computed(() => false)
    const isVip = computed(() => false)
    const hasPermission = computed(() => {
      if (!chapterData.value) return false
      if (chapterData.value.error) return false
      return true
    })
    const readerImages = computed(() => {
      if (!chapterData.value || !Array.isArray(chapterData.value.images)) {
        return []
      }
      // 后台第一张是原有的剧情提示图，统一改由版权图承载，阅读时隐藏原图。
      return chapterData.value.images.filter(Boolean).slice(1)
    })
    const hasChapterImages = computed(() => {
      return !!(chapterData.value && Array.isArray(chapterData.value.images) && chapterData.value.images.some(Boolean))
    })
    
    // UI控制
    const hideUI = ref(false)
    const toggleUI = () => {
      hideUI.value = !hideUI.value
    }

    // 全屏控制
    const isFullscreen = ref(false)
    const toggleFullscreen = () => {
      if (!document.fullscreenElement) {
        const fullscreenTarget = readerPageRef.value || document.documentElement
        fullscreenTarget.requestFullscreen().then(() => {
          isFullscreen.value = true
        }).catch(err => {
          console.error('全屏模式出错:', err)
        })
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen().then(() => {
            isFullscreen.value = false
          }).catch(err => {
            console.error('退出全屏模式出错:', err)
          })
        }
      }
    }

    // 点击内容区域也可以切换UI显示
    const handleContentClick = (event) => {
      // 避免点击到链接和按钮时触发
      if (event.target.tagName !== 'BUTTON' && 
          event.target.tagName !== 'A' && 
          !event.target.closest('.chapter-dropdown')) {
        toggleUI()
      }
    }
    
    // 获取章节详情
    const fetchChapterDetail = async () => {
      loading.value = true;
      // 初始化chapterData为基本结构，避免模板中的null引用错误
      chapterData.value = {
        error: true,
        errorType: 'loading',
        title: ''
      };
      
      // 检查参数
      const chapterIdToUse = chapterId.value || route.params.id;
      console.log('===DEBUG===: 开始获取章节详情, 参数:', chapterIdToUse);
      
      if (!chapterIdToUse) {
        console.error('===DEBUG===: 章节ID未定义，无法获取章节详情');
        chapterData.value = {
          error: true,
          errorType: 'unknown',
          message: '章节ID未定义',
          title: '未知章节'
        };
        loading.value = false;
        return;
      }
      
      try {
        const response = await api.getChapterDetail(chapterIdToUse);
        console.log('===DEBUG===: 章节详情API响应:', response);
        
        if (response && response.code >= 1000) {
          // 青少年模式限制码（1001 锁定 / 1002 内容分级 / 1003 夜间禁用 / 1004 付费 / 1005 社区）
          console.log('===DEBUG===: 青少年模式限制:', response.code);
          if (response.code === YOUTH_CODE_LOCKED) {
            router.replace('/youth-protection');
          }
          chapterData.value = {
            error: true,
            errorType: 'youth',
            message: response.msg || youthErrorText[response.code] || '青少年模式下不可阅读本章',
            title: response.data?.title || response.data?.comic_title || '受限章节'
          };
        } else if (response && response.code === 1) {
          chapterData.value = response.data;
          currentPageIndex.value = 0
          pagedShowsCopyright.value = true
          console.log('===DEBUG===: 设置章节数据:', chapterData.value);
          
          // 检查图片数组
          if (chapterData.value && Array.isArray(chapterData.value.images)) {
            console.log('===DEBUG===: 图片数量:', chapterData.value.images.length);
            // 预处理所有图片的加载状态为false
            resetImageState()
            chapterData.value.images.filter(Boolean).slice(1).forEach((_, index) => {
              imageLoaded[index + 1] = false;
              imageFailed[index + 1] = false;
            });
          } else {
            console.error('===DEBUG===: 章节数据中没有图片数组或格式不正确');
          }
        } else if (response && response.code === 2) {
          // 会员章节
          console.log('===DEBUG===: 会员章节');
          chapterData.value = {
            error: true,
            errorType: 'permission',
            need_pay: true,
            title: response.data?.title || `第${chapterId.value}话`
          };
        } else if (response && response.code === 3) {
          // VIP章节
          console.log('===DEBUG===: VIP章节');
          chapterData.value = {
            error: true,
            errorType: 'vip',
            title: response.data?.title || `第${chapterId.value}话`
          };
        } else {
          console.error('===DEBUG===: API返回错误:', response);
          // 未知错误
          chapterData.value = {
            error: true,
            errorType: 'unknown',
            message: response?.msg || '未知错误',
            title: response?.data?.title || `第${chapterId.value}话`
          };
        }
      } catch (error) {
        console.error('===DEBUG===: 获取章节详情失败', error);
        
        // 处理常见错误情况
        const errorMsg = error.message || '未知错误';
        
        // 设置会员权限错误标志
        if (errorMsg.includes('开通VIP') || errorMsg.includes('付费内容') || error?.data?.need_vip) {
          chapterData.value = {
            error: true,
            errorType: 'permission',
            need_pay: true,
            title: `第${chapterId.value}话`
          };
        } else if (errorMsg.includes('登录')) {
          chapterData.value = {
            error: true,
            errorType: 'login',
            title: `第${chapterId.value}话`
          };
        } else {
          chapterData.value = {
            error: true,
            errorType: 'unknown',
            message: errorMsg,
            title: `第${chapterId.value}话`
          };
        }
      } finally {
        console.log('===DEBUG===: 加载完成, 最终chapterData状态:', chapterData.value);
        loading.value = false;
      }
    }
    
    // 获取章节列表
    const fetchChapterList = async () => {
      // 检查参数
      const comicIdToUse = comicId.value || route.params.id
      console.log('===DEBUG===: 开始获取章节列表, 参数:', comicIdToUse)
      
      if (!comicIdToUse) {
        console.error('===DEBUG===: 漫画ID未定义，无法获取章节列表')
        return
      }
      
      try {
        const response = await api.getComicChapters(comicIdToUse)
        console.log('===DEBUG===: 章节列表API响应:', response)
        if (response && response.code === 1) {
          chapterList.value = response.data.list || []
          console.log('===DEBUG===: 设置章节列表数据, 长度:', chapterList.value.length)
          // 设置漫画标题
          if (response.data.comic) {
            comicTitle.value = response.data.comic.title || ''
            console.log('===DEBUG===: 设置漫画标题:', comicTitle.value)
          }
        } else {
          console.error('===DEBUG===: API返回错误:', response)
        }
      } catch (error) {
        console.error('===DEBUG===: 获取章节列表失败', error)
      }
    }
    
    const fetchComicInfo = async () => {
      const comicIdToUse = comicId.value || route.params.id
      if (!comicIdToUse) return

      try {
        const response = await api.getComicDetail(comicIdToUse)
        if (response && response.code === 1 && response.data) {
          comicInfo.value = response.data
          comicTitle.value = response.data.title || comicTitle.value
          return
        }
        console.error('===DEBUG===: 获取作品信息失败', response)
      } catch (error) {
        console.error('===DEBUG===: 获取作品信息失败', error)
      }
    }

    // 图片加载完成
    const handleImageLoaded = (displayIndex) => {
      imageLoaded[displayIndex] = true
      // 版权提示页或第一张漫画图加载完成后尝试恢复阅读进度
      if (displayIndex === 0 || displayIndex === 1) {
        loadProgress()
      }
    }

    const handleImageError = (displayIndex, event) => {
      imageLoaded[displayIndex] = true
      imageFailed[displayIndex] = true
      const img = event && event.target
      if (!img || img.dataset.fallbackApplied) return
      img.dataset.fallbackApplied = '1'
      img.src = placeholderImage
    }

    const resetImageState = () => {
      Object.keys(imageLoaded).forEach(key => delete imageLoaded[key])
      Object.keys(imageFailed).forEach(key => delete imageFailed[key])
    }

    const getReaderImageUrl = (image) => {
      if (!image) return placeholderImage
      if (/^https?:\/\//i.test(image) || image.startsWith('data:') || image.startsWith('blob:')) return image
      if (image.startsWith('//')) return window.location.protocol + image
      if (image.startsWith('/')) return baseUrl + image
      return `${baseUrl}/${image}`
    }
    
    // 返回漫画详情页
    const goBack = () => {
      // 确保使用正确的漫画ID
      const comicIdToUse = comicId.value || route.params.id
      console.log('===DEBUG===: 返回漫画详情页，使用ID:', comicIdToUse)
      
      if (comicIdToUse) {
        router.push(`/comic/${comicIdToUse}`)
      } else if (chapterList.value && chapterList.value.length > 0 && chapterList.value[0].comic_id) {
        // 尝试从章节列表获取漫画ID
        router.push(`/comic/${chapterList.value[0].comic_id}`)
      } else {
        // 无法确定漫画ID，返回分类页
        router.push('/category')
      }
    }
    
    // 上一章
    const goPrevChapter = () => {
      if (hasPrevChapter.value) {
        const prevChapter = chapterList.value[currentChapterIndex.value - 1]
        if (prevChapter && prevChapter.id) {
          console.log('===DEBUG===: 切换到上一章:', prevChapter.id, prevChapter.title)
          // 确保ID是数字
          const prevChapterId = parseInt(prevChapter.id)
          switchChapter(prevChapterId)
        } else {
          console.error('===DEBUG===: 上一章数据无效', prevChapter)
        }
      } else {
        console.log('===DEBUG===: 已经是第一章')
      }
    }
    
    // 下一章
    const goNextChapter = () => {
      if (hasNextChapter.value) {
        const nextChapter = chapterList.value[currentChapterIndex.value + 1]
        if (nextChapter && nextChapter.id) {
          console.log('===DEBUG===: 切换到下一章:', nextChapter.id, nextChapter.title)
          // 确保ID是数字
          const nextChapterId = parseInt(nextChapter.id)
          switchChapter(nextChapterId)
        } else {
          console.error('===DEBUG===: 下一章数据无效', nextChapter)
        }
      } else {
        console.log('===DEBUG===: 已经是最后一章')
      }
    }
    
    // 切换章节
    const switchChapter = (newChapterId) => {
      // 隐藏章节列表
      showChapterList.value = false
      
      // 更新路由 - 使用正确的路由格式
      const comicIdToUse = comicId.value || route.params.id
      console.log('===DEBUG===: 切换章节, 从', chapterId.value, '到', newChapterId, '漫画ID:', comicIdToUse)
      
      if (comicIdToUse) {
        // 使用replace而不是push，避免历史堆栈过多
        router.replace(`/read/${comicIdToUse}/${newChapterId}`)
        
        // 更新状态 - 由于可能路由没变，强制更新状态和内容
        chapterId.value = newChapterId
        
        // 重新获取章节数据
        fetchChapterDetail()
      } else {
        console.error('===DEBUG===: 切换章节失败，无法确定漫画ID')
        alert('无法确定漫画ID，请返回详情页重试')
      }
    }
    
    // 前往登录页
    const goToLogin = () => {
      router.push(`/login?redirect=/read/${comicId.value}/${chapterId.value}`)
    }
    
    // 前往VIP页面
    const goToVip = () => {
      router.push('/user?tab=vip')
    }
    
    // 前往青少年模式设置页
    const goYouthProtection = () => {
      router.push('/youth-protection')
    }

    // ===== 阅读设置（背景主题/翻页模式）与页码进度 =====
    const settings = reactive({
      theme: localStorage.getItem('reader_theme') || 'default',
      mode: localStorage.getItem('reader_mode') || 'scroll'
    })
    const showSettings = ref(false)
    const currentPageIndex = ref(0)
    const pagedShowsCopyright = ref(true)
    const pendingProgress = ref(1)
    let progressTimer = null

    const visibleImages = computed(() => {
      if (settings.mode === 'paged') {
        if (pagedShowsCopyright.value) {
          return [copyrightNoticeImageUrl.value]
        }
        const idx = Math.min(currentPageIndex.value, Math.max(0, readerImages.value.length - 1))
        return readerImages.value.length ? [readerImages.value[idx]] : []
      }
      return [copyrightNoticeImageUrl.value, ...readerImages.value]
    })

    const visibleImageDisplayIndex = (index) => {
      if (settings.mode === 'paged') {
        return pagedShowsCopyright.value ? 0 : currentPageIndex.value + 1
      }
      return index
    }

    const visibleImageDataIndex = (index) => {
      const displayIndex = visibleImageDisplayIndex(index)
      return displayIndex === 0 ? 'copyright' : displayIndex - 1
    }

    const pagedIndicatorText = computed(() => {
      const total = readerImages.value.length + 1
      return pagedShowsCopyright.value
        ? `版权声明 / ${total}`
        : `${currentPageIndex.value + 2} / ${total}`
    })

    const applyTheme = (theme) => {
      settings.theme = theme
      localStorage.setItem('reader_theme', theme)
    }

    const applyMode = (mode) => {
      const previousMode = settings.mode
      settings.mode = mode
      localStorage.setItem('reader_mode', mode)
      if (mode === 'paged') {
        if (previousMode !== 'paged') pagedShowsCopyright.value = true
        currentPageIndex.value = Math.min(currentPageIndex.value, Math.max(0, readerImages.value.length - 1))
      }
    }

    const goPage = (delta) => {
      if (pagedShowsCopyright.value) {
        if (delta > 0 && readerImages.value.length > 0) {
          pagedShowsCopyright.value = false
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        return
      }

      if (delta < 0 && currentPageIndex.value === 0) {
        pagedShowsCopyright.value = true
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }

      const maxIndex = Math.max(0, readerImages.value.length - 1)
      const next = Math.max(0, Math.min(maxIndex, currentPageIndex.value + delta))
      if (next === currentPageIndex.value) return
      currentPageIndex.value = next
      saveProgress(next + 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const saveProgress = (page) => {
      if (!localStorage.getItem('token')) return
      const comicIdToUse = comicId.value || route.params.id
      if (!comicIdToUse || !chapterId.value) return
      pendingProgress.value = page
      if (progressTimer) return
      progressTimer = setTimeout(() => {
        progressTimer = null
        api.saveProgress({
          comic_id: comicIdToUse,
          chapter_id: chapterId.value,
          page: pendingProgress.value
        }).catch(err => console.error('保存进度失败:', err))
      }, 1500)
    }

    const loadProgress = async () => {
      const comicIdToUse = comicId.value || route.params.id
      if (!localStorage.getItem('token') || !comicIdToUse || !chapterId.value) return
      try {
        const res = await api.getProgress(comicIdToUse, chapterId.value)
        const page = res && res.code === 1 && res.data ? parseInt(res.data.page) : 1
        if (page >= 1 && readerImages.value.length > 0) {
          const idx = Math.min(page - 1, readerImages.value.length - 1)
          if (settings.mode === 'paged') {
            // 单页模式始终先展示版权页，点击“下一页”后再进入恢复到的阅读位置。
            currentPageIndex.value = idx
          } else if (page > 1) {
            setTimeout(() => {
              const el = document.querySelector(`.image-container[data-index="${idx}"]`)
              if (el && el.scrollIntoView) {
                el.scrollIntoView({ behavior: 'auto', block: 'start' })
              }
            }, 300)
          }
        }
      } catch (error) {
        console.error('加载阅读进度失败:', error)
      }
    }

    // 滚动模式：记录当前可见图片的页码
    const handleScrollProgress = () => {
      if (settings.mode !== 'scroll' || readerImages.value.length === 0) return
      const containers = document.querySelectorAll('.image-container')
      if (!containers.length) return
      let current = 0
      const threshold = window.innerHeight * 0.3
      for (let i = 0; i < containers.length; i++) {
        const rect = containers[i].getBoundingClientRect()
        const dataIndex = containers[i].dataset.index
        if (dataIndex !== 'copyright' && rect.top <= threshold) {
          current = parseInt(dataIndex)
        } else {
          break
        }
      }
      saveProgress(current + 1)
    }
    
    // 监听全屏状态变化
    const handleFullscreenChange = () => {
      isFullscreen.value = !!document.fullscreenElement;
    };

    // 监听点击外部关闭章节列表
    const handleDocumentClick = (event) => {
      // 如果章节列表显示中，且点击的不是章节列表本身或其子元素
      if (showChapterList.value && 
          !event.target.closest('.chapter-select')) {
        showChapterList.value = false;
      }
    };

    onMounted(() => {
      console.log('===DEBUG===: 组件挂载, 路由参数:', route.params)
      fetchChapterDetail()
      fetchChapterList()
      
      fetchComicInfo()

      // 监听全屏状态改变
      document.addEventListener('fullscreenchange', handleFullscreenChange);
      
      // 添加点击文档关闭章节列表的监听
      document.addEventListener('click', handleDocumentClick);

      // 滚动模式：记录阅读进度
      window.addEventListener('scroll', handleScrollProgress, { passive: true });
    })

    onUnmounted(() => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      
      // 移除点击文档关闭章节列表的监听
      document.removeEventListener('click', handleDocumentClick);

      window.removeEventListener('scroll', handleScrollProgress);
    })
    
    return {
      loading,
      chapterData,
      chapterList,
      comicTitle,
      chapterTitle,
      readerPageRef,
      comicId,
      chapterId,
      showChapterList,
      imageLoaded,
      imageFailed,
      readerImages,
      currentChapterIndex,
      hasPrevChapter,
      hasNextChapter,
      isLoggedIn,
      isVipChapter,
      isVip,
      hasPermission,
      hasChapterImages,
      fetchChapterDetail,
      fetchChapterList,
      handleContentClick,
      handleImageLoaded,
      handleImageError,
      getReaderImageUrl,
      goBack,
      goPrevChapter,
      goNextChapter,
      switchChapter,
      goToLogin,
      goToVip,
      goYouthProtection,
      baseUrl,
      placeholderImage,
      hideUI,
      toggleUI,
      isFullscreen,
      toggleFullscreen,
      settings,
      showSettings,
      currentPageIndex,
      visibleImages,
      pagedShowsCopyright,
      pagedIndicatorText,
      visibleImageDisplayIndex,
      visibleImageDataIndex,
      applyTheme,
      applyMode,
      goPage
    }
  }
}
</script>

<style scoped>
.reader-page {
  width: 100%;
  min-height: 100vh;
  background-color: #121212;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

.reader-page:fullscreen {
  width: 100vw;
  height: 100vh;
  min-height: 100vh;
  margin: 0;
  overflow-y: auto;
  background-color: #121212;
}

.reader-page:fullscreen .reader-header,
.reader-page:fullscreen .reader-footer {
  left: 0;
}

.reader-header {
  position: fixed;
  top: 0;
  left: 236px;
  right: 0;
  background-color: rgba(25, 25, 25, 0.95);
  backdrop-filter: blur(10px);
  padding: 15px 20px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.25);
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transform: translateY(0);
  transition: transform 0.3s ease;
}

.header-hidden {
  transform: translateY(-100%);
}

.header-left {
  display: flex;
  align-items: center;
}

.back-btn {
  background-color: #2a2a2a;
  border: none;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #fff;
  margin-right: 15px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background-color: #ff6b6b;
}

.arrow {
  margin-right: 5px;
}

.comic-info {
  display: flex;
  flex-direction: column;
}

.comic-title {
  font-size: 16px;
  font-weight: bold;
  color: #fff;
  margin-bottom: 5px;
}

.chapter-title {
  font-size: 14px;
  color: #aaa;
}

.chapter-nav {
  display: flex;
  align-items: center;
}

.prev-chapter, .next-chapter {
  padding: 8px 16px;
  border: none;
  background-color: #2a2a2a;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
}

.prev-chapter:hover, .next-chapter:hover {
  background-color: #ff6b6b;
}

.prev-chapter:disabled, .next-chapter:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #2a2a2a;
}

.chapter-select {
  margin: 0 10px;
  position: relative;
}

.current-chapter {
  padding: 8px 16px;
  background-color: #2a2a2a;
  border: none;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s ease;
}

.current-chapter:hover {
  background-color: #3a3a3a;
}

.arrow-down {
  font-size: 12px;
  margin-left: 8px;
  transition: transform 0.2s ease;
}

.chapter-select.active .arrow-down {
  transform: rotate(180deg);
}

.chapter-dropdown {
  display: none;
}

.chapter-sidebar {
  position: fixed;
  top: 0;
  right: -300px;
  width: 300px;
  height: 100vh;
  background-color: #2a2a2a;
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  transition: right 0.3s ease;
  display: flex;
  flex-direction: column;
}

.sidebar-open {
  right: 0;
}

.sidebar-header {
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #3a3a3a;
}

.sidebar-header h3 {
  margin: 0;
  color: #fff;
  font-size: 18px;
}

.close-sidebar {
  background: none;
  border: none;
  color: #aaa;
  font-size: 24px;
  cursor: pointer;
}

.close-sidebar:hover {
  color: #ff6b6b;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 10px 0;
  scrollbar-width: thin;
  scrollbar-color: #444 #2a2a2a;
}

.sidebar-content::-webkit-scrollbar {
  width: 6px;
}

.sidebar-content::-webkit-scrollbar-track {
  background: #2a2a2a;
}

.sidebar-content::-webkit-scrollbar-thumb {
  background-color: #444;
  border-radius: 6px;
}

.sidebar-chapter-item {
  padding: 12px 15px;
  cursor: pointer;
  border-bottom: 1px solid #3a3a3a;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sidebar-chapter-item:hover {
  background-color: #3a3a3a;
}

.sidebar-chapter-item.active {
  background-color: #3a3a3a;
  border-left: 4px solid #ff6b6b;
}

.sidebar-chapter-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #ddd;
  font-size: 14px;
}

.sidebar-chapter-item.active .sidebar-chapter-title {
  color: #ff6b6b;
  font-weight: bold;
}

.sidebar-chapter-tag {
  display: flex;
  gap: 8px;
}

.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.reader-content {
  flex: 1;
  padding: 70px 0 50px 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.loading, .no-permission {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 400px;
  width: 100%;
  max-width: 800px;
  background-color: #2a2a2a;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.25);
  margin-top: 50px;
  color: #fff;
}

.no-permission h3 {
  font-size: 22px;
  margin-bottom: 15px;
  color: #ff6b6b;
}

.no-permission p {
  font-size: 16px;
  color: #aaa;
  margin-bottom: 25px;
  max-width: 80%;
  text-align: center;
}

.action-buttons {
  display: flex;
  gap: 15px;
}

.action-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  background-color: #ff6b6b;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  font-size: 15px;
}

.action-btn:hover {
  background-color: #ff5252;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(255, 107, 107, 0.3);
}

.reader-work-content {
  width: min(100%, 800px);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.work-attribution {
  padding: 18px 20px;
  color: #f5f7ff;
  border: 1px solid rgba(255, 204, 102, 0.26);
  border-radius: 10px;
  background:
    linear-gradient(120deg, rgba(255, 204, 102, 0.13), rgba(35, 211, 238, 0.1)),
    #171922;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.24);
}

.work-attribution-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
}

.work-attribution-heading span,
.work-attribution-grid span {
  color: #9faaca;
  font-size: 13px;
}

.work-attribution-heading strong {
  overflow: hidden;
  color: #ffdc7b;
  font-size: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.work-attribution-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.work-attribution-grid p {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 0;
}

.work-attribution-grid strong {
  color: #f5f7ff;
  font-size: 14px;
}

.work-attribution small {
  display: block;
  margin-top: 14px;
  color: #9faaca;
  font-size: 12px;
}

.comic-images {
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  background-color: #1a1a1a;
  padding: 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.3);
}

.image-container {
  width: 100%;
  position: relative;
  background-color: #1a1a1a;
  overflow: hidden;
}

.comic-image {
  width: 100%;
  display: block;
  margin: 0 auto;
}

.image-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(26, 26, 26, 0.85);
}

.reader-footer {
  position: fixed;
  bottom: 0;
  left: 236px;
  right: 0;
  background-color: rgba(25, 25, 25, 0.95);
  backdrop-filter: blur(10px);
  padding: 15px 20px;
  box-shadow: 0 -2px 15px rgba(0, 0, 0, 0.25);
  transform: translateY(0);
  transition: transform 0.3s ease;
  z-index: 100;
}

.footer-hidden {
  transform: translateY(100%);
}

.footer-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 800px;
  margin: 0 auto;
}

.chapter-info {
  font-size: 15px;
  color: #aaa;
  font-weight: 500;
}

/* 全屏模式切换按钮 */
.toggle-ui-btn, .fullscreen-btn {
  position: fixed;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 2px solid rgba(255, 255, 255, 0.76);
  border-radius: 50%;
  color: #07101d;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1000;
  transition: all 0.3s ease;
  font: inherit;
  line-height: 1;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.42);
}

.toggle-ui-btn {
  right: 20px;
  bottom: 80px;
  background: linear-gradient(135deg, #ffcc66, #23d3ee);
}

.fullscreen-btn {
  right: 20px;
  bottom: 130px;
  background: #1d2b4a;
  color: #f5f7ff;
  border-color: #23d3ee;
}

.toggle-ui-btn:hover, .fullscreen-btn:hover {
  transform: scale(1.1);
  filter: brightness(1.18);
}

.toggle-ui-btn span, .fullscreen-btn span {
  display: block;
  font-size: 20px;
  font-weight: 900;
}

.header-hidden {
  opacity: 0;
  pointer-events: none;
}

.footer-hidden {
  opacity: 0;
  pointer-events: none;
}

@media (max-width: 768px) {
  .reader-work-content {
    width: 100%;
  }

  .work-attribution {
    margin: 0 12px;
    padding: 16px;
  }

  .work-attribution-grid {
    grid-template-columns: 1fr;
    gap: 9px;
  }

  .comic-images {
    border-radius: 0;
    box-shadow: none;
  }
  
  .reader-header {
    padding: 10px 15px;
  }
  
  .header-right {
    width: 100%;
  }
  
  .chapter-nav {
    width: 100%;
  }
  
  .comic-title, .chapter-title {
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .action-btn {
    padding: 8px 15px;
    font-size: 14px;
  }
  
  .chapter-dropdown {
    width: 100%;
    max-width: 360px;
    left: auto;
    right: 0;
  }
  
  .chapter-dropdown-title {
    max-width: 55%;
  }
  
  .toggle-ui-btn {
    bottom: 70px;
    right: 15px;
  }
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  border-left-color: #ff6b6b;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.chapter-tag {
  font-size: 12px;
  padding: 3px 6px;
  border-radius: 4px;
  font-weight: 600;
  display: inline-block;
  text-align: center;
  min-width: 40px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.chapter-tag.free {
  background-color: rgba(24, 144, 255, 0.2);
  color: #1890ff;
  border: 1px solid rgba(24, 144, 255, 0.3);
}

.chapter-tag.vip {
  background-color: rgba(250, 140, 22, 0.2);
  color: #fa8c16;
  border: 1px solid rgba(250, 140, 22, 0.3);
}

.chapter-tag.paid {
  background-color: rgba(255, 77, 79, 0.2);
  color: #ff4d4f;
  border: 1px solid rgba(255, 77, 79, 0.3);
}
</style>

<style scoped>
/* ===== 阅读设置：主题/翻页/进度 ===== */
.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
}

@media (max-width: 920px) {
  .reader-header,
  .reader-footer {
    left: 0;
  }
}

.reader-page.theme-default {
  background: #0b0d14;
}

.reader-page.theme-night {
  background: #05070b;
}

.reader-page.theme-paper {
  background: #f3ecd9;
}

.reader-page.theme-paper .reader-header,
.reader-page.theme-paper .reader-footer {
  background: #e8dfc8;
  color: #3a3226;
}

.reader-page.theme-paper .back-btn,
.reader-page.theme-paper .prev-chapter,
.reader-page.theme-paper .next-chapter,
.reader-page.theme-paper .current-chapter {
  background-color: #3a3226 !important;
  color: #f7f1e3 !important;
}

.reader-page.theme-paper .comic-title {
  color: #3a3226 !important;
}

.reader-page.theme-paper .chapter-title {
  color: #6f6653 !important;
}

/* 设置面板护眼配色：深字浅底 */
.reader-page.theme-paper .reader-settings-panel {
  background: #efe8d5 !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
}

.reader-page.theme-paper .settings-head h3 {
  color: #3a3226 !important;
}

.reader-page.theme-paper .settings-close {
  color: #f7f1e3 !important;
  background: #3a3226 !important;
}

.reader-page.theme-paper .setting-row > span {
  color: #5f574a !important;
}

.reader-page.theme-paper .setting-options button {
  color: #3a3226 !important;
  border-color: rgba(0, 0, 0, 0.3) !important;
  background: rgba(0, 0, 0, 0.06) !important;
}

.reader-page.theme-paper .setting-options button.active {
  color: #07101d !important;
  border: 0 !important;
  background: linear-gradient(135deg, #ffcc66, #23d3ee) !important;
}

.reader-settings-btn {
  min-width: 72px;
  height: 40px;
  margin-left: 10px;
  padding: 0 10px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  color: #dfe7ff;
  font-size: 17px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.reader-settings-label {
  font-size: 13px;
  font-weight: 700;
}

.reader-page.theme-paper .reader-settings-btn {
  color: #3a3226;
  border-color: rgba(0, 0, 0, 0.14);
}

.reader-settings-mask {
  position: fixed;
  inset: 0;
  z-index: 2600;
  display: grid;
  place-items: center;
  padding: 18px;
  background: rgba(0, 0, 0, 0.62);
  backdrop-filter: blur(4px);
}

.reader-settings-panel {
  width: min(420px, 100%);
  padding: 22px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: #121625;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.5);
}

.settings-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
}

.settings-head h3 {
  color: #f5f7ff;
}

.settings-close {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  color: #dfe7ff;
  font-size: 20px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.08);
}

.setting-row {
  display: grid;
  gap: 10px;
  margin-bottom: 18px;
}

.setting-row > span {
  color: #9faaca;
  font-size: 13px;
  font-weight: 800;
}

.setting-options {
  display: flex;
  gap: 10px;
}

.setting-options button {
  flex: 1;
  min-height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  color: #dfe7ff;
  font-weight: 800;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.06);
}

.setting-options button.active {
  color: #07101d;
  border: 0;
  background: linear-gradient(135deg, #ffcc66, #23d3ee);
}

.comic-images.paged .image-container {
  display: flex;
  justify-content: center;
}

.paged-nav {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
  margin: 18px 0 6px;
}

.paged-prev,
.paged-next {
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  color: #dfe7ff;
  font-weight: 800;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.08);
}

.paged-prev:disabled,
.paged-next:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.paged-indicator {
  color: #ffcc66;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
}

.reader-page.theme-paper .paged-prev,
.reader-page.theme-paper .paged-next {
  color: #3a3226;
  border-color: rgba(0, 0, 0, 0.14);
  background: rgba(0, 0, 0, 0.06);
}

.reader-page.theme-paper .paged-indicator {
  color: #5f574a;
}
</style> 
